import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { ErrorComponent } from 'app/shared/components/error.component';
import { SaveButtonComponent } from 'app/shared/components/save-button.component';
import { SessionPhotoSource } from 'app/shared/graph';
import { StoreLocalesService } from 'app/shared/store-locales/store-locales.service';
import { TranslatableLabelFieldComponent } from 'app/shared/store-locales/translatable-label-field.component';
import { blobToBase64 } from 'app/shared/utilities/file-helpers';

import { ExternalPhotosConfigService } from './external-photos-config.service';

@Component({
    selector: 'app-session-photo-source-edit-page',
    standalone: true,
    templateUrl: './session-photo-source-edit-page.component.html',
    styleUrls: ['./session-photo-source-edit-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        SaveButtonComponent,
        PageSimpleContentComponent,
        FormsModule,
        ErrorComponent,
        MatCheckbox,
        MatIcon,
        TranslatableLabelFieldComponent,
    ],
})
export class SessionPhotoSourceEditPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly snackBar = inject(MatSnackBar);
    private readonly photosConfigService = inject(ExternalPhotosConfigService);
    private readonly storeLocalesService = inject(StoreLocalesService);

    public labelTranslations = signal<Record<string, string>>({});
    public supportedLocales = signal<string[]>([]);
    public labelLocalizationKey = signal<string>('');
    public labelValue = signal<string>('');

    PageStatus = PageStatus;
    photoSourceIdControl = new FormControl('', [Validators.required, Validators.pattern('[a-z0-9]+')]);
    thumbnailUrlControl = new FormControl('', [Validators.required]);
    enabledControl = new FormControl(true);
    mode: 'edit' | 'create' = 'edit';
    storeId = '';
    photoSourceId = '';
    sessionPhotoSource?: SessionPhotoSource;
    private redirectTimerId: number | undefined;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.photoSourceId = params.photoSourceId;
            this.mode = this.route.snapshot.data.mode;
            this.labelLocalizationKey.set('hub.sources.session.' + this.photoSourceId + '.label');

            this.storeLocalesService.getStoreLocalizedLabel(this.storeId, this.labelLocalizationKey()).subscribe((translations) => {
                this.labelTranslations.set(translations);
                this.labelValue.set(this.labelTranslations()['en'] ?? 'Session Photos');
            });
            this.storeLocalesService.listStoreSupportedLocales(this.storeId).subscribe((locales) => {
                this.supportedLocales.set(locales);
            });

            if (this.mode === 'edit') {
                this.subscribeWithGenericLoadingErrorHandling(
                    this.photosConfigService.loadSessionPhotoSource(this.storeId, this.photoSourceId),
                    source => {
                        if (source) {
                            this.sessionPhotoSource = source;
                            this.photoSourceIdControl.setValue(source.name);
                            this.thumbnailUrlControl.setValue(source.thumbnailUrl);
                            this.enabledControl.setValue(source.enabled);
                        }
                        this.setPageStatus(PageStatus.loaded);
                    },
                );
            } else {
                this.sessionPhotoSource = {
                    name: this.photoSourceId,
                    thumbnailUrl: '',
                    enabled: true,
                } as SessionPhotoSource;
                this.photoSourceIdControl.setValue(this.photoSourceId);
                this.thumbnailUrlControl.setValue('');
                this.enabledControl.setValue(false);
                this.setPageStatus(PageStatus.loaded);
            }
        }));
    }

    ngOnDestroy(): void {
        this.unsubscribeSubscriptions();
        if (this.redirectTimerId) {
            clearTimeout(this.redirectTimerId);
        }
    }

    save(): void {
        if (!this.storeId) {
            return;
        }

        const name = this.photoSourceIdControl.value?.trim();
        const thumbnailUrl = this.thumbnailUrlControl.value?.trim();

        if (!name || !thumbnailUrl) {
            return;
        }
        if (!this.photoSourceIdControl.valid || !this.thumbnailUrlControl.valid) {
            return;
        }

        if (this.sessionPhotoSource) {
            this.sessionPhotoSource.name = name;
            this.sessionPhotoSource.thumbnailUrl = thumbnailUrl;
        }

        const input = { storeId: this.storeId, name, thumbnailUrl, enabled: this.enabledControl.value || false };

        if (this.mode === 'edit') {
            this.subscribeWithGenericSavingErrorHandling(
                forkJoin([
                    this.storeLocalesService.updateStoreLocalizedLabel(this.storeId, this.labelLocalizationKey(), this.labelTranslations()),
                    this.photosConfigService.updateSessionPhotoSource(input),
                ]),
                () => this.completeSave(),
            );
        } else {
            this.subscribeWithGenericSavingErrorHandling(
                forkJoin([
                    this.storeLocalesService.updateStoreLocalizedLabel(this.storeId, this.labelLocalizationKey(), this.labelTranslations()),
                    this.photosConfigService.createSessionPhotoSource(input),
                ]),
                () => this.completeSave(),
            );
        }
    }

    protected async onThumbnailSelected(files: FileList | null) {
        if (!files) {
            return;
        }
        let file = files[0];
        let fileExtension = file.name.split('.').pop();
        if (!fileExtension) {
            return;
        }
        this.photosConfigService.uploadAssetEditorFile({
            storeId: this.storeId,
            filename: 'photo-sources/session.' + this.photoSourceId + '.thumbnail.' + fileExtension,
            contentBase64: await blobToBase64(file),
        }).subscribe({
            next: (result) => {
                let url = new URL(result.publicUrl);
                if (result.contentHash) {
                    url.searchParams.set('h', result.contentHash);
                }
                this.thumbnailUrlControl.setValue(url.toString());
            },
            error: (e: Error) => {
                this.snackBar.open('Failed to upload thumbnail: ' + e.message, 'Dismiss');
            }
        });
    }

    private completeSave(): void {
        this.pageStatus = PageStatus.loaded;
        this.redirectTimerId = window.setTimeout(async () => {
            await this.router.navigate(['stores', this.storeId, 'settings', 'external-photo-sources']);
        }, 500);
    }
}
